import { useQuery } from '@apollo/client'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Icon, IconType, ListOptions, Loader } from '../../../components'
import {
  GetCollaborationsByCampaignIdResponse,
  GET_COLLABORATIONS_BY_CAMPAIGN_ID,
} from '../../../gql'
import {
  CollaborationStatus,
  GetCollaborationsOptions,
  GetCollaborationsSort,
  QueryGetCollaborationsByCampaignIdArgs,
  User,
} from '../../../gql/types'
import { Header } from '../components/Header'
import { List } from './components/List'
import { Grid } from './components/Grid'

interface IProps {
  user: User
}

type TabOption = {
  id: string
  label: string
  isFavorite: boolean | null
}

const CollaboratorTabs = ({
  activeTab,
  onTabChange,
}: {
  activeTab: string
  onTabChange: (tab: TabOption) => void
}) => {
  const tabs: TabOption[] = [
    {
      id: 'all',
      label: 'All',
      isFavorite: null,
    },
    {
      id: 'shortlisted',
      label: 'Shortlisted',
      isFavorite: true,
    },
  ]

  return (
    <div className='border-b border-gray-200'>
      <nav className='-mb-px flex space-x-8 px-4' aria-label='Tabs'>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab)}
            className={`
              whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
              ${
                activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
            aria-current={activeTab === tab.id ? 'page' : undefined}
          >
            {tab.label}
          </button>
        ))}
      </nav>
    </div>
  )
}

const filterOptions = [
  {
    id: 'isFavorite',
    name: 'Favorites',
    options: [
      {
        value: true,
        label: 'View Favorite Collaborators',
        // icon: <Icon type={IconType.heartFilled} className='h-4 w-4 text-red-500' aria-hidden='true'/>
      },
      // { value: false, label: 'Not Favorites'},
    ],
  },
]

const Collaborators = (_props: IProps) => {
  const { campaignId } = useParams()

  const limit = 40
  const [skip, setSkip] = useState(0)

  const [query, setQuery] = useState('')
  const [filtersValue, setFiltersValue] = useState({
    status: {
      [CollaborationStatus.creatorApplied]: false,
      [CollaborationStatus.creatorAccepted]: true,
      [CollaborationStatus.productShipped]: true,
      [CollaborationStatus.productDelivered]: true,
      [CollaborationStatus.contentUploaded]: true,
      [CollaborationStatus.contentAccepted]: true,
      [CollaborationStatus.contentRejected]: true,
    },
    isFavorite: {
      ['true']: false,
      ['false']: false,
    },
  })

  const [activeTab, setActiveTab] = useState('all')
  const [favoriteFilter, setFavoriteFilter] = useState<boolean | null>(null)

  const { loading, error, data, refetch } = useQuery<
    GetCollaborationsByCampaignIdResponse,
    QueryGetCollaborationsByCampaignIdArgs
  >(GET_COLLABORATIONS_BY_CAMPAIGN_ID, {
    variables: {
      campaignId: Number(campaignId),
      options: {
        query: '',
        filters: {
          status: [
            CollaborationStatus.creatorAccepted,
            CollaborationStatus.productShipped,
            CollaborationStatus.productDelivered,
            CollaborationStatus.contentUploaded,
            CollaborationStatus.contentAccepted,
            CollaborationStatus.contentRejected,
          ],
          isFavorite: favoriteFilter,
        },
        sort: GetCollaborationsSort.lastUpdated,
        skip,
        limit,
      },
    },
    notifyOnNetworkStatusChange: true,
  })

  // TODO: Fix mixed reactive/manual pattern antipattern
  // Option 1: Pure manual (like Products) - remove favoriteFilter from useQuery variables
  // Option 2: Hybrid reactive (like Campaigns) - remove this useEffect, keep reactive useQuery + local state for UX
  // Current issue: useQuery is reactive to favoriteFilter BUT useEffect also calls refetch() = double API calls
  useEffect(() => {
    refetch()
  }, [skip, refetch, favoriteFilter])

  const refetchData = (query, filters) => {
    const isFavoriteFilters = Object.keys(filters.isFavorite).filter(
      (key) => filters.isFavorite[key] === true,
    )

    refetch({
      options: {
        query: query,
        filters: {
          status: Object.keys(filters.status).filter(
            (key) => filters.status[key] === true,
          ) as CollaborationStatus[],
          isFavorite:
            isFavoriteFilters.length === 1
              ? isFavoriteFilters[0] === 'true'
              : favoriteFilter,
        },
        sort: GetCollaborationsSort.lastUpdated,
        skip: 0,
        limit,
      },
    })
  }

  const handleTabChange = (tab: TabOption) => {
    setActiveTab(tab.id)
    setFavoriteFilter(tab.isFavorite)

    // Reset pagination when changing tabs
    setSkip(0)
  }

  const onSearch = () => {
    refetchData(query, filtersValue)
  }

  const setPage = (page: number) => {
    setSkip(page * limit)
  }

  const onFiltersUpdate = (section, option) => {
    const newFiltersValue = {
      ...filtersValue,
      [section.id]: {
        ...filtersValue[section.id],
        [option.value]: !filtersValue[section.id][option.value],
      },
    }

    setFiltersValue(newFiltersValue)
    refetchData(query, newFiltersValue)
  }

  if (error) {
    console.log(error)
    return <p>There has been an error. Try refreshing the page</p>
  }

  if (!loading && !data?.getCollaborationsByCampaignId) {
    return <p>Campaign does not exist.</p>
  }

  return (
    <div>
      <Header />

      {loading && <Loader />}
      {!loading && (
        <div className='pb-4 w-auto h-fit'>
          <div className='overflow-x-auto'>
            <div className='inline-block min-w-full py-2 px-1 align-middle'>
              <div className='shadow ring-1 ring-black ring-opacity-5 rounded-t'>
                <CollaboratorTabs
                  activeTab={activeTab}
                  onTabChange={handleTabChange}
                />
                <ListOptions
                  query={query}
                  setQuery={setQuery}
                  onSearch={onSearch}
                  filterOptions={[]}
                  filtersValue={filtersValue}
                  onFiltersUpdate={onFiltersUpdate}
                  isSearchBarFullWidth={true}
                  className='p-2'
                />
                <div className='hidden lg:block'>
                  <List
                    collaborations={data?.getCollaborationsByCampaignId.items}
                    pagination={data?.getCollaborationsByCampaignId?.pagination}
                    setPage={setPage}
                    onRefetch={() => refetch()}
                  />
                </div>
                <div className='block lg:hidden'>
                  <Grid
                    collaborations={data?.getCollaborationsByCampaignId.items}
                    pagination={data?.getCollaborationsByCampaignId?.pagination}
                    setPage={setPage}
                    onRefetch={() => refetch()}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export { Collaborators }
